import {Children, isValidElement} from 'react'
import type {ReactElement, ReactNode} from 'react'
import type {Components} from 'react-markdown'
import {highlightChildren} from '@/components/Docs/docsComponents'
import {extractText, slugify} from '@/components/Docs/docsUtils'

/**
 * Markdown formatter for the in-page docs slider only. The full Docs page keeps `getMarkdownComponents`.
 * Authoring conventions that rely on it are described in `docs/in-page-docs/style-guide.md`:
 *  - `##` is a section, `###` is a titled entry (a KPI or concept) followed by a short description
 *  - a paragraph that holds only inline code is rendered as a formula line
 *  - a blockquote is a callout; its leading bold word becomes the label
 */
export const PAGE_DOCS_FONT_FAMILY = "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif"

interface Palette {
  heading: string
  body: string
  muted: string
  border: string
  divide: string
  surface: string
  chip: string
  chipRing: string
  link: string
  marker: string
  quoteLabel: string
}

const getPalette = (isDark: boolean): Palette =>
  isDark
    ? {
        heading: 'text-[var(--color-text-primary)]',
        body: 'text-[var(--color-text-secondary)]',
        muted: 'text-[var(--color-text-secondary)]/65',
        border: 'border-[var(--color-border-default)]',
        divide: 'divide-[var(--color-border-default)]',
        surface: 'bg-[var(--color-background-layer-02)]',
        chip: 'bg-[var(--color-background-layer-02)] text-[var(--color-text-primary)]',
        chipRing: 'ring-1 ring-[var(--color-border-default)] ring-inset',
        link: 'text-[var(--color-link-default)] decoration-current/40 hover:decoration-current',
        marker: 'marker:text-[var(--color-text-secondary)]',
        quoteLabel: '[&>p:first-child>strong:first-child]:text-[var(--color-text-secondary)]/65',
      }
    : {
        heading: 'text-slate-900',
        body: 'text-slate-700',
        muted: 'text-slate-500',
        border: 'border-slate-200',
        divide: 'divide-slate-200',
        surface: 'bg-slate-50',
        chip: 'bg-slate-100 text-slate-800',
        chipRing: '',
        link: 'text-blue-700 decoration-blue-300 hover:decoration-blue-700',
        marker: 'marker:text-slate-400',
        quoteLabel: '[&>p:first-child>strong:first-child]:text-slate-500',
      }

export const getPageDocsMarkdownComponents = (isDark: boolean, highlightQuery = ''): Components => {
  const c = getPalette(isDark)
  const mark = (children: ReactNode): ReactNode =>
    highlightQuery === '' ? children : highlightChildren(children, highlightQuery)

  const Code: Components['code'] = ({className, children}) => {
    const raw = String(children)
    const isBlock = className !== undefined || raw.endsWith('\n')
    if (isBlock) {
      return <code className={className}>{raw.replace(/\n$/, '')}</code>
    }
    return <code className={`rounded px-1.5 py-0.5 text-[0.88em] font-medium ${c.chip}`}>{children}</code>
  }

  return {
    h2: ({children}) => (
      <h2
        id={slugify(extractText(children))}
        className={`mt-9 scroll-mt-2 border-b pb-2 text-[19px] leading-7 font-semibold tracking-[-0.01em] first:mt-0 ${c.border} ${c.heading}`}
      >
        {mark(children)}
      </h2>
    ),
    h3: ({children}) => (
      <h3
        id={slugify(extractText(children))}
        className={`mt-5 scroll-mt-2 text-[15px] leading-6 font-semibold [h2+&]:mt-4 ${c.heading}`}
      >
        {mark(children)}
      </h3>
    ),
    p: ({children}) => {
      const nodes = Children.toArray(children)
      const only = nodes.length === 1 ? nodes[0] : null
      if (isValidElement(only) && only.type === Code) {
        const formula = (only as ReactElement<{children?: ReactNode}>).props.children
        return (
          <p className="mt-2 first:mt-0">
            <span
              className={`inline-block max-w-full rounded-md px-2.5 py-1.5 text-[13px] leading-5 font-medium break-words ${c.chip} ${c.chipRing}`}
            >
              {mark(formula)}
            </span>
          </p>
        )
      }
      return (
        <p className={`mt-2.5 text-[14px] leading-[1.65] first:mt-0 [h2+&]:mt-3 [h3+&]:mt-1 ${c.body}`}>
          {mark(children)}
        </p>
      )
    },
    ul: ({children}) => (
      <ul className={`mt-2.5 list-disc space-y-1.5 pl-5 text-[14px] leading-[1.65] ${c.body} ${c.marker}`}>
        {children}
      </ul>
    ),
    ol: ({children}) => (
      <ol className={`mt-2.5 list-decimal space-y-1.5 pl-5 text-[14px] leading-[1.65] ${c.body} ${c.marker}`}>
        {children}
      </ol>
    ),
    li: ({children}) => <li className="pl-0.5 [&>p]:mt-0">{mark(children)}</li>,
    strong: ({children}) => <strong className={`font-semibold ${c.heading}`}>{children}</strong>,
    a: ({href, children}) => {
      const isExternal = href?.startsWith('http') === true
      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className={`font-medium underline underline-offset-[3px] transition-colors ${c.link}`}
        >
          {children}
        </a>
      )
    },
    blockquote: ({children}) => (
      <blockquote
        className={`mt-4 rounded-lg border px-4 py-3 text-[13.5px] leading-[1.6] ${c.border} ${c.surface} ${c.body} [&>p]:mt-0 [&>p+p]:mt-2 [&>p:first-child>strong:first-child]:mb-1 [&>p:first-child>strong:first-child]:block [&>p:first-child>strong:first-child]:text-[11px] [&>p:first-child>strong:first-child]:font-semibold [&>p:first-child>strong:first-child]:tracking-[0.06em] [&>p:first-child>strong:first-child]:uppercase ${c.quoteLabel}`}
      >
        {children}
      </blockquote>
    ),
    table: ({children}) => (
      <div className={`mt-3 overflow-x-auto rounded-lg border ${c.border}`}>
        <table className={`w-full border-collapse text-[13px] ${c.body}`}>{children}</table>
      </div>
    ),
    thead: ({children}) => <thead className={c.surface}>{children}</thead>,
    tbody: ({children}) => <tbody className={`divide-y ${c.divide}`}>{children}</tbody>,
    tr: ({children}) => <tr>{children}</tr>,
    th: ({children}) => (
      <th
        className={`px-3 py-2 text-left text-[11px] font-semibold tracking-[0.06em] whitespace-nowrap uppercase ${c.muted}`}
      >
        {mark(children)}
      </th>
    ),
    td: ({children}) => (
      <td className="px-3 py-2 align-top text-[13px] leading-5 first:font-medium first:whitespace-nowrap [&_a]:no-underline [&_a:hover]:underline">
        {mark(children)}
      </td>
    ),
    hr: () => null,
    pre: ({children}) => (
      <pre
        className={`mt-3 overflow-x-auto rounded-lg border px-4 py-3 font-mono text-[12.5px] leading-6 ${c.border} ${c.surface} ${c.body}`}
      >
        {children}
      </pre>
    ),
    code: Code,
  }
}
